import { Link, NavLink, useLocation, matchPath } from 'react-router-dom';
import { useMinimizeContext } from '../../context/LayoutContext';
import { sidebarData } from '../../config/sidebarData';
import { UserContext } from '../../context/UserContext';
import { useContext, useEffect, useState } from 'react';
import { PremiumIcon } from '../../svg';
import { useTranslation } from 'react-i18next';
import useSubscription from '../../hooks/useSubscription';
import useBrandAccess from '../../hooks/useBrandAccess';
import useRole from '../../hooks/useRole';
import ToolTip from '../common/ToolTip';

const Sidebar = () => {
  const { t } = useTranslation();
  const { minimize } = useMinimizeContext();
  const { user, campaignStats } = useContext(UserContext) || {};
  const { hasSubscription } = useSubscription();
  const { canAccessFeature } = useBrandAccess();
  const { hasRole } = useRole();
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});

  const location = useLocation();
  const editFrom = location.state?.from;
  const fromMenu = location.state?.fromMenu;
  const fromTab = location.state?.fromTab;

  // useEffect(() => {
  //   const fetchCampaignStats = async () => {
  //     if (user?.role === 'brandUser' && user.brand?._id) {
  //       try {
  //         const response = await getCampaignStatsByBrand(user.brand._id);
  //         setCampaignStats(response);
  //       } catch (error) {
  //         console.error('Error fetching campaign stats:', error);
  //         // En cas d'erreur, définir des valeurs par défaut
  //         setCampaignStats({
  //           live: 0,
  //           scheduled: 0,
  //           completed: 0,
  //           paused: 0,
  //           total: 0,
  //         });
  //       }
  //     }
  //   };
  //   fetchCampaignStats();
  // }, [user]);

  useEffect(() => {
    const newOpenMenus: { [key: string]: boolean } = {};

    sidebarData.forEach((item) => {
      if (!item.subMenu) return;

      const isSubmenuActive = item.subMenu.some((subItem) =>
        matchPath({ path: subItem.route, end: false }, location.pathname),
      );

      const isBaseRouteActive =
        item.baseRoute &&
        matchPath(
          { path: `${item.baseRoute}/*`, end: false },
          location.pathname,
        );

      // ✅ Explicitly handle campaign detail & edit pages
      const isCampaignMenu = item.label === 'campaign';

      const isCampaignSection =
        isCampaignMenu &&
        (location.pathname.startsWith('/campaign/') ||
          location.pathname.startsWith('/edit-campaign/') ||
          fromMenu === 'campaign');
      if (isSubmenuActive || isBaseRouteActive || isCampaignSection) {
        newOpenMenus[item.label] = true;
      }
    });

    setOpenMenus(newOpenMenus);
  }, [location.pathname, fromMenu]);

  const toggleMenu = (label: string) => {
    setOpenMenus((prev) => {
      const newOpenMenus: { [key: string]: boolean } = {};
      newOpenMenus[label] = !prev[label];
      return newOpenMenus;
    });
  };

  const shouldShowMenuItem = (item: (typeof sidebarData)[0]): boolean => {
    if (!user) return false;

    const hasRequiredRole = item.type.some((role) => hasRole(role));
    const hasAccess = canAccessFeature(item.access);
    return hasRequiredRole && hasAccess;
  };

  // const shouldShowSubMenuItem = (
  //   subItem: (typeof sidebarData)[0]['subMenu'][0],
  // ) => {
  //   if (!canAccessFeature(subItem.access)) return false;

  //   if (campaignStats) {
  //     if (subItem.label.includes('Active') && campaignStats.live === 0)
  //       return false;
  //     if (subItem.label.includes('Scheduled') && campaignStats.scheduled === 0)
  //       return false;
  //     if (subItem.label.includes('Completed') && campaignStats.completed === 0)
  //       return false;
  //     if (subItem.label.includes('Analytics') && campaignStats.total === 0)
  //       return false;
  //   }

  //   return true;
  // };
  const safeCampaignStats = campaignStats ?? {
    live: 0,
    draft: 0,
    paused: 0,
    scheduled: 0,
    completed: 0,
    total: 0,
  };
  const hasAnyCampaign =
    safeCampaignStats.live > 0 ||
    safeCampaignStats.draft > 0 ||
    safeCampaignStats.paused > 0;

  const shouldShowSubMenuItem = (
    subItem: NonNullable<(typeof sidebarData)[0]['subMenu']>[number],
  ) => {
    // Check if user has access to the feature
    if (!canAccessFeature(subItem.access)) return false;

    // Allow specific submenu items to be shown regardless of campaign stats
    const alwaysVisibleItems = [
      'createnew',
      'mylinks',
      'bulkupload',
      'analytics',
      'activebrandlicenses',
      'activebrands',
      'brandwaitlist',
      'creatorsWaitlist',
      'creatorPayouts',
      'smartlinkusers',
      'flaggedSubmissions',
      'cashbackLedger',
      'bonusLedger',
      'flaggedsubmissions',
      'allsubmissions',
      'allcampaigns',
      'allbrands',
      'allcreators',
      'cashbackLedger',
    ];
    if (alwaysVisibleItems.some((label) => subItem.label.includes(label))) {
      return true;
    }
    if (subItem.label.includes('active')) {
      if (hasAnyCampaign) return true;
      if (location.pathname.startsWith('/campaign/')) {
        return true;
      }
      if (location.pathname.startsWith('/active-campaign')) {
        return true;
      }
      if (
        location.pathname.startsWith('/edit-campaign') &&
        editFrom === 'active'
      ) {
        return true;
      }

      return false;
    }
    if (subItem.label.includes('active')) {
      if (hasAnyCampaign) return true;

      if (
        location.pathname.startsWith('/edit-campaign') &&
        editFrom === 'active'
      ) {
        return true;
      }

      return false;
    }
    if (subItem.label.includes('scheduled')) {
      return (
        safeCampaignStats.scheduled > 0 ||
        location.pathname.startsWith('/scheduled-campaign')
      );
    }
    if (subItem.label.includes('completed')) {
      return safeCampaignStats.completed > 0;
    }
    if (subItem.label.includes('analytics')) {
      return safeCampaignStats.total > 0;
    }

    return false;
  };
  const isAnySubMenuActive = (item: (typeof sidebarData)[0]) => {
    console.log('Checking active submenu for item:', item);
    const isSubmenuRouteActive = item.subMenu?.some((subItem) =>
      matchPath({ path: subItem.route, end: false }, location.pathname),
    );

    // 🔒 Only Campaign menu should react to these routes
    const isCampaignMenu = item.label === 'campaign';
    const isUserDetailFromCampaign =
      isCampaignMenu &&
      location.pathname.startsWith('/user-detail') &&
      fromMenu === 'campaign';
    const isEditCampaign = location.pathname.startsWith('/edit-campaign/');
    const isEditFromCorrectTab =
      isEditCampaign &&
      (editFrom === 'active' ||
        editFrom === 'scheduled' ||
        editFrom === 'completed' ||
        !editFrom);

    const isCampaignDetailPage =
      isCampaignMenu && location.pathname.startsWith('/campaign/');

    const isBaseRouteActive =
      item.baseRoute &&
      matchPath({ path: `${item.baseRoute}/*`, end: false }, location.pathname);

    return (
      isSubmenuRouteActive ||
      isCampaignDetailPage ||
      isBaseRouteActive ||
      (isCampaignMenu && isEditFromCorrectTab) ||
      isUserDetailFromCampaign
    );
  };

  const memberRole =
    user?.brand?.members?.find((member) => member.userId === user._id)?.role ||
    '';
  const isUserRole = memberRole === 'user';
  return (
    <div
      className={`relative flex flex-col justify-between w-full ${minimize ? 'p-[24px]' : 'py-[24px]'} h-full`}
    >
      <ul className="">
        {sidebarData.map((val, index) => {
          if (!shouldShowMenuItem(val)) return null;

          const hasSubMenu = !!val.subMenu?.length;
          const isOpen = openMenus[val.label];

          return (
            <li key={index}>
              {val.route && !hasSubMenu ? (
                <NavLink
                  to={val.route}
                  className={({ isActive }) =>
                    `flex items-center cursor-pointer py-[12px] border-l-4 ${
                      !minimize ? 'px-[5px] lg:px-[44px]' : ''
                    } font-content ${
                      isActive
                        ? 'bg-gray-100 text-[#020D3A] border-[#020D3A]'
                        : 'bg-transparent border-transparent text-[#4D494F] hover:bg-gray-100'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {minimize ? (
                        <ToolTip
                          text={String(t(`common.${val.label}`))}
                          position="right"
                        >
                          <img
                            src={isActive ? val?.activeIcon : val?.icon}
                            alt={String(t(`common.${val.label}`))}
                            className={`ml-[10px] mr-[12px]`}
                          />
                        </ToolTip>
                      ) : (
                        <img
                          src={isActive ? val?.activeIcon : val?.icon}
                          alt={String(t(`common.${val.label}`))}
                          className={`ml-[10px] mr-[12px]`}
                        />
                      )}
                      {!minimize && (
                        <div className="flex items-center justify-between w-full">
                          <div>
                            <span className="whitespace-nowrap">
                              {t(`common.${val.label}`)}
                            </span>
                          </div>
                          <div>
                            {val.access === 'limited' && !hasSubscription && (
                              <PremiumIcon />
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </NavLink>
              ) : hasSubMenu ? (
                <div
                  onClick={() => toggleMenu(val.label)}
                  className={`relative flex items-center cursor-pointer py-[12px] border-l-4 ${
                    !minimize ? 'px-[5px] lg:px-[44px]' : ''
                  } font-content ${
                    isAnySubMenuActive(val)
                      ? 'bg-gray-100 text-[#020D3A] border-[#020D3A]'
                      : 'bg-transparent text-[#4D494F] border-transparent hover:bg-gray-100'
                  }`}
                >
                  {minimize ? (
                    <ToolTip
                      text={String(t(`common.${val.label}`))}
                      position="right"
                    >
                      <img
                        src={isOpen ? val?.activeIcon : val?.icon}
                        alt={String(t(`common.${val.label}`))}
                        className={`ml-[10px] mr-[12px]`}
                      />
                    </ToolTip>
                  ) : (
                    <img
                      src={isOpen ? val?.activeIcon : val?.icon}
                      alt={String(t(`common.${val.label}`))}
                      className={`ml-[10px] mr-[12px]`}
                    />
                  )}
                  {!minimize && (
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <span className="whitespace-nowrap">
                          {t(`common.${val.label}`)}
                        </span>
                      </div>
                      <div>
                        {isOpen ? (
                          <img
                            src="/assets/fichevron-up.svg"
                            alt="chevron-up"
                            className={`ml-[10px] mr-[12px]`}
                          />
                        ) : (
                          <img
                            src="/assets/fichevron-down.svg"
                            alt="chevron-down"
                            className={`ml-[10px] mr-[12px]`}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className={`flex items-center cursor-pointer py-[12px] ${
                    !minimize && 'px-[5px] lg:px-[20]'
                  } rounded-full font-content bg-transparent text-[#afafaf]`}
                >
                  <img
                    src={val?.icon}
                    alt={String(t(`common.${val.label}`))}
                    className={`ml-[10px] mr-[12px]`}
                  />
                  <div className="flex flex-col lg:flex-row">
                    <span className={`${minimize ? 'hidden' : 'block'} mr-2`}>
                      {t(`common.${val.label}`)}
                    </span>
                    <span
                      className={`${
                        minimize ? 'hidden' : 'block'
                      } text-secondary font-bold`}
                    >
                      {t('common.soon')}
                    </span>
                  </div>
                </div>
              )}
              {hasSubMenu && isOpen && (
                <ul className="submenu-list">
                  {val.subMenu
                    ?.filter(shouldShowSubMenuItem)
                    .map((subItem, subIdx) => (
                      <li key={subIdx}>
                        <NavLink
                          to={
                            isUserRole && subItem.label === 'createnew'
                              ? '#'
                              : subItem.route
                          }
                          onClick={(e) => {
                            if (isUserRole && subItem.label === 'createnew') {
                              e.preventDefault();
                            }
                          }}
                          className={() => {
                            const isEditCampaign =
                              location.pathname.startsWith('/edit-campaign/');

                            const isCampaignDetailPage =
                              location.pathname.startsWith('/campaign/');
                            const detailFrom = location.state?.from;
                            const isDetailActive =
                              isCampaignDetailPage &&
                              subItem.label === (detailFrom || 'active');
                            const isEditActive =
                              isEditCampaign &&
                              ((subItem.label === 'active' &&
                                editFrom === 'active') ||
                                (subItem.label === 'scheduled' &&
                                  editFrom === 'scheduled') ||
                                (subItem.label === 'completed' &&
                                  editFrom === 'completed') ||
                                (!editFrom && subItem.label === 'active'));

                            const isUserDetailFromCampaignTab =
                              location.pathname.startsWith('/user-detail') &&
                              fromMenu === 'campaign' &&
                              !!fromTab &&
                              subItem.label === fromTab;
                            const isActive =
                              matchPath(
                                { path: subItem.route, end: false },
                                location.pathname,
                              ) ||
                              isDetailActive ||
                              isEditActive ||
                              isUserDetailFromCampaignTab;

                            const isDisabled =
                              isUserRole && subItem.label === 'createnew';

                            return `flex items-center py-[12px] ${
                              !minimize ? 'px-[5px] lg:px-[80px]' : ''
                            } font-content ${
                              isDisabled
                                ? 'text-gray-400 cursor-not-allowed opacity-60 border-transparent'
                                : isActive
                                  ? 'bg-gray-100 text-[#020D3A] border-[#020D3A]'
                                  : 'bg-transparent text-[#4D494F] hover:bg-gray-100 border-transparent'
                            }`;
                          }}
                        >
                          {({ isActive }) => (
                            <>
                              {minimize ? (
                                <ToolTip
                                  text={String(t(`common.${subItem.label}`))}
                                  position="right"
                                >
                                  <img
                                    src={
                                      isActive
                                        ? subItem?.activeIcon
                                        : subItem?.icon
                                    }
                                    alt={String(t(`common.${val.label}`))}
                                    className={`ml-[10px] mr-[12px]`}
                                  />
                                </ToolTip>
                              ) : (
                                <img
                                  src={
                                    isActive
                                      ? subItem?.activeIcon
                                      : subItem?.icon
                                  }
                                  alt={String(t(`common.${val.label}`))}
                                  className={`ml-[10px] mr-[12px]`}
                                />
                              )}
                              {!minimize && (
                                <div className="flex items-center justify-between w-full">
                                  <div>
                                    <span className="whitespace-nowrap">
                                      {t(`common.${subItem.label}`)}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </NavLink>
                      </li>
                    ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Sidebar;
